'use client';

import useSWR, { mutate } from 'swr';
import { useMemo } from 'react';
import FilterBuilder from 'utils/filterBuilder';
import { apiOslogCrud } from 'helpers/oslogFormatEndpoint';
// import { normalizeApiResponse } from 'utils/response';

const areaApi = apiOslogCrud('area');
const fetcher = async ([url, payload]) => {
  const response = await areaApi.search(payload);
  return response;
};

export const revalidateAreaList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/area/search');
};

export const useAreaList = (page, rowsPerPage, searchKey, searchValue, companyId = '') => {
  const start = page * rowsPerPage;
  const tableName = searchKey === 'country' ? 'm_country' : null;
  const filterLike = searchKey === 'country' ? 'name' : searchKey;

  const payload = useMemo(() => {
    const fb = new FilterBuilder().setPaging(start, rowsPerPage).addJoin('company', ['name']).addJoin('country', ['name']);

    if (searchValue && searchValue.trim() !== '') {
      fb.like(filterLike, searchValue, 'AND', tableName);
    }
    if (companyId) {
      const idStr = String(companyId);
      if (idStr.includes(',')) {
        fb.in('company_id', idStr);
      } else {
        fb.equal('company_id', idStr);
      }
    } else {
      fb.filterByCompany();
    }
    return fb.build();
  }, [start, rowsPerPage, searchValue, companyId, filterLike, tableName]);
  const key = useMemo(() => ['/area/search', JSON.stringify(payload)], [payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true,
    revalidateOnMount: true
  });

  return {
    data: data?.data,
    totalRecord: data?.totalRecord,
    error,
    isLoading
  };
};

export const getAreaById = async (id) => {
  if (id == null) throw new Error('id is required');

  try {
    const response = await areaApi.getById(id);
    return response.data ?? null;
  } catch (error) {
    console.error('Failed to fetch area by ID:', error);
    throw error;
  }
};

export const createArea = async (payload) => {
  try {
    const response = await areaApi.create(payload);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateAreaList();
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateArea = async (id, payload) => {
  try {
    const response = await areaApi.update(id, payload);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateAreaList();
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteArea = async (id) => {
  try {
    const response = await areaApi.remove(id);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateAreaList();
    return response.data;
  } catch (error) {
    throw error;
  }
};
